/* CAMPAIGN_ROUTE_v2 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';

const card = { background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:20, marginBottom:16 };
const lbl = { fontSize:12, color:'#6b7280', display:'block', marginBottom:4 };
const th = { fontSize:11, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.03em' };
const chipBase = { padding:'4px 11px', borderRadius:8, border:'1px solid #d1d5db', fontSize:12, cursor:'pointer', background:'#fff', color:'#374151' };
const chipOn = { ...chipBase, background:'#dbeafe', border:'1px solid #2563eb', color:'#1e40af', fontWeight:600 };
const warn = { background:'#fef9c3', border:'1px solid #fde047', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#854d0e', marginBottom:14 };
const btn = { padding:'6px 14px', borderRadius:8, border:'1px solid #d1d5db', background:'#fff', cursor:'pointer', fontSize:13 };

const n = v => Number(v || 0);
const fmt = v => n(v).toLocaleString();
const pct = (a,b) => n(b) ? Math.round(n(a)*100/n(b)) + '%' : '0%';
const dt = s => s ? new Date(s).toLocaleString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '';

/* TILE_CLICK_v1 */
function Tile({ value, label, danger, onClick, active }) {
  return (
    <div onClick={onClick} style={{ flex:1, minWidth:92, border: active ? '1px solid #2563eb' : '1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', cursor: onClick ? 'pointer' : 'default', background: danger ? '#fee2e2' : (active ? '#dbeafe' : '#fff') }}>
      <div style={{ fontSize:18, color: danger ? '#991b1b' : '#111' }}>{value}</div>
      <div style={{ fontSize:11, color: danger ? '#991b1b' : '#6b7280' }}>{label}</div>
    </div>
  );
}

function trail(r) {
  const t = [];
  if (r.sent_at) t.push('Sent ✓');
  if (r.delivered_at) t.push('Delivered ✓');
  if (r.opened_at) t.push('Opened ✓');
  if (r.activated_at) t.push('Engaged ✓');
  if (r.status === 'BOUNCED') t.push(r.error ? String(r.error).slice(0,40) : 'Bounced');
  return t.join(' · ');
}

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [msg, setMsg] = useState('');
  const [sel, setSel] = useState(null);
  const [sends, setSends] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('');
  const [fkind, setFkind] = useState(''); /* FILTER_KIND_v1 */
  const LIMIT = 50;

  const loadSends = useCallback(async (off, st, fk) => {
    try {
      const q = '?limit=' + LIMIT + '&offset=' + off
        + (st ? '&status=' + encodeURIComponent(st) : '')
        + (fk ? '&filter=' + encodeURIComponent(fk) : '');
      const d = await client.get('/admin/marketing/campaigns/' + id + '/sends' + q);
      setSends(Array.isArray(d.data.rows) ? d.data.rows : []);
      setTotal(n(d.data.total));
    } catch (e) { setMsg('Failed to load recipients'); }
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const d = await client.get('/admin/marketing/campaigns/results');
        const list = Array.isArray(d.data.campaigns) ? d.data.campaigns : [];
        const hit = list.find(c => String(c.id) === String(id));
        if (hit) setSel(hit); else setMsg('Campaign not found');
      } catch (e) { setMsg('Failed to load campaign'); }
    })();
    loadSends(0, '', '');
  }, [id, loadSends]);

  const filter = (st) => { setStatus(st); setFkind(''); setOffset(0); loadSends(0, st, ''); };
  const kfilter = (fk) => { setFkind(fk); setStatus(''); setOffset(0); loadSends(0, '', fk); };
  const page = (off) => { setOffset(off); loadSends(off, status, fkind); };
  const bouncePct = c => n(c.sent) ? n(c.bounced)*100/n(c.sent) : 0;

  return (
    <div style={{ maxWidth:1100, margin:'0 auto', padding:'20px 16px' }}>
      <button style={btn} onClick={() => nav('/engagement-leads')}>← All campaigns</button>
      <div style={{ height:14 }} />
      {msg && <div style={warn}>{msg}</div>}
      {!sel && !msg && <div style={{ fontSize:13, color:'#6b7280' }}>Loading campaign...</div>}
      {sel && (
        <div style={card}>
          <span style={lbl}>Selected</span>
          <div style={{ fontSize:15, color:'#111' }}>{sel.name}</div>
          <div style={{ fontSize:12, color:'#6b7280', marginBottom:12 }}>#{sel.id} · {dt(sel.sent_at)} · {sel.segment}</div>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
            <Tile value={fmt(sel.sent)} label="SENT" onClick={() => filter('')} active={status === '' && fkind === ''} />
            <Tile value={fmt(sel.delivered)} label={'DELIVERED ' + pct(sel.delivered, sel.sent)} onClick={() => filter('DELIVERED')} active={status === 'DELIVERED'} />
            <Tile value={fmt(sel.bounced)} label={'BOUNCED ' + pct(sel.bounced, sel.sent)} danger={bouncePct(sel) > 4} onClick={() => filter('BOUNCED')} active={status === 'BOUNCED'} />
            <Tile value={fmt(sel.opened)} label={'OPENED ' + pct(sel.opened, sel.delivered)} onClick={() => kfilter('opened')} active={fkind === 'opened'} />
            <Tile value={fmt(sel.engaged)} label="ENGAGED" onClick={() => kfilter('engaged')} active={fkind === 'engaged'} />
            <Tile value={fmt(sel.signed_up)} label="SIGNED UP" onClick={() => kfilter('signed_up')} active={fkind === 'signed_up'} />
          </div>

          <div style={warn}>
            {fmt(sel.scanner_noise)} link fetches came from mail scanners and are not counted as Engaged · {fmt(sel.unsubscribed)} unsubscribed
          </div>

          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
            {['', 'DELIVERED', 'OPENED', 'CLICKED', 'BOUNCED', 'SENT'].map(st => (
              <span key={st || 'ALL'} style={status === st && !fkind ? chipOn : chipBase} onClick={() => filter(st)}>
                {st === '' ? 'All ' + fmt(sel.sent) : st.charAt(0) + st.slice(1).toLowerCase()}
              </span>
            ))}
          </div>

          <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
            <div style={{ display:'flex', padding:'8px 14px', borderBottom:'1px solid #e5e7eb' }}>
              <div style={{ flex:1, ...th }}>Recipient</div>
              <div style={{ width:96, ...th }}>Status</div>
              <div style={{ width:230, ...th }}>Trail</div>
            </div>
            {sends.length === 0 && <div style={{ padding:14, fontSize:13, color:'#6b7280' }}>No recipients for this filter.</div>}
            {sends.map(r => (
              <div key={r.id} style={{ display:'flex', alignItems:'center', padding:'10px 14px', borderBottom:'1px solid #f3f4f6' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:'#2563eb', overflow:'hidden', textOverflow:'ellipsis' }}>{r.email}</div>
                  <div style={{ fontSize:11, color:'#6b7280' }}>{r.business_name || r.contact_name || r.source}</div>
                </div>
                <div style={{ width:96 }}>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:8, background: r.status === 'BOUNCED' ? '#fee2e2' : (r.status === 'CLICKED' || r.status === 'OPENED' ? '#dbeafe' : '#f3f4f6'), color: r.status === 'BOUNCED' ? '#991b1b' : (r.status === 'CLICKED' || r.status === 'OPENED' ? '#1e40af' : '#374151') }}>{r.status}</span>
                </div>
                <div style={{ width:230, fontSize:11, color:'#6b7280' }}>{trail(r)}</div>
              </div>
            ))}
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, fontSize:12, color:'#6b7280' }}>
            <span>{total ? (offset+1) + '–' + Math.min(offset+LIMIT, total) + ' of ' + fmt(total) : '0'}</span>
            <span>
              {offset > 0 && <span style={{ color:'#2563eb', cursor:'pointer', marginRight:14 }} onClick={() => page(Math.max(0, offset-LIMIT))}>← Previous</span>}
              {offset + LIMIT < total && <span style={{ color:'#2563eb', cursor:'pointer' }} onClick={() => page(offset+LIMIT)}>Next {LIMIT} →</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
